import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useOperator } from "@/stores/operator-store";

import OperatorsIcon from "@/components/OperatorIcons";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { RadioTower } from "lucide-react";
import Pulse from "./Pulse";

const OperatorsStatus = ({ isNew = false }: { isNew?: boolean }) => {
  const { status } = useOperator();
  const hasUnavailableService = status.some(
    (operator) =>
      !operator.status || operator.setting.some((service) => !service.status),
  );

  return (
    <>
      {isNew && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="hidden h-9 max-w-full gap-2 bg-card/80 px-2 md:flex xl:hidden"
            >
              <RadioTower className="size-4 shrink-0" />
              <span className="truncate">وضعیت اپراتورها</span>
              <Pulse
                isActive={!hasUnavailableService}
                isPending={hasUnavailableService}
                size="size-2"
              />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            className="w-72 max-w-[calc(100vw-1rem)] p-2"
          >
            <DropdownMenuLabel>وضعیت اپراتورها</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="max-h-[min(28rem,70vh)] overflow-y-auto">
              {status.map((operator) => (
                <section
                  key={operator.name}
                  className="border-b border-border px-2 py-3 last:border-b-0"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-2">
                      <OperatorsIcon
                        operator={operator.name}
                        className="size-7 shrink-0"
                        operatorColors
                      />
                      <span className="truncate text-sm font-medium">
                        {operator.title}
                      </span>
                    </div>
                    <Pulse
                      isActive={operator.status}
                      isPending={operator.setting.some(
                        (service) => !service.status,
                      )}
                      size="size-2"
                    />
                  </div>
                  <div className="mt-2 grid gap-1.5 border-r border-border pr-3">
                    {operator.setting.map((service) => (
                      <div
                        key={service.title}
                        className="flex items-center justify-between gap-3 text-xs text-muted-foreground"
                      >
                        <span className="truncate">{service.title}</span>
                        <Pulse isActive={service.status} size="size-1.5" />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <NavigationMenu
        className={`mb-4 md:mb-0 ${isNew ? "hidden xl:flex" : "hidden md:flex"}`}
      >
        <NavigationMenuList className="flex-nowrap">
          {status.map((ss) => (
            <NavigationMenuItem key={ss.name}>
              <NavigationMenuTrigger
                className={`flex gap-1 ${isNew ? "bg-transparent px-2 2xl:px-4" : ""} cursor-pointer`}
              >
                {isNew ? (
                  <>
                    <Pulse
                      isActive={ss.status}
                      isPending={
                        ss.setting.filter((s) => s.status !== true).length > 0
                      }
                      size="size-2"
                    />
                    {ss.title}
                  </>
                ) : (
                  <>
                    {ss.title}
                    <Pulse
                      isActive={ss.status}
                      isPending={
                        ss.setting.filter((s) => s.status !== true).length > 0
                      }
                      size="size-2"
                    />
                  </>
                )}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <Card className="w-80 md:w-96">
                  <CardHeader className="flex justify-between items-center flex-row">
                    <CardTitle>{ss.title}</CardTitle>
                    <Pulse isActive={ss.status} />
                  </CardHeader>
                  <CardContent className="flex flex-row space-x-2 space-x-reverse">
                    <OperatorsIcon
                      operator={ss.name}
                      className="size-20"
                      operatorColors
                    />
                    <div className="flex flex-col w-full border-r pr-2 border-gray-200">
                      {ss.setting.map((s) => (
                        <div className="my-2" key={s.title}>
                          <div className="flex items-center w-full justify-between">
                            {s.title}
                            <Pulse isActive={s.status} />
                          </div>
                          <Separator />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </NavigationMenuContent>
            </NavigationMenuItem>
          ))}
        </NavigationMenuList>
      </NavigationMenu>
    </>
  );
};

export default OperatorsStatus;
